'use client';
import dynamic from 'next/dynamic';
import type { FC, PropsWithChildren } from 'react';
import { Suspense, useCallback, useMemo, useRef, useState } from 'react';

import { useAuthActionsContext } from '~context/auth';
import type {
	PaymentReason,
	PaymentReasonType,
} from '~features/annals/enums/payment';
import { RegisterInbound } from '~features/annals/enums/register';
import annalsCheckout from '~features/annals/hooks/checkout';
import {
	BenefitsModal,
	MAX_COUNT_TO_SHOW_BENEFITS,
	MIN_COUNT_TO_SHOW_BENEFITS,
} from '~features/benefits';
import { NeuronsModal, SubscriptionModal } from '~features/paywall';
import { loadFromStorage, lsKeys, saveToStorage } from '~features/storage';
import { useHasSubscription } from '~features/tags';
import { useModal } from '~hooks/useModal';
import { usePurchase } from '~hooks/usePurchase';
import { useSession } from '~hooks/useSession';
import ClientLogger from '~logger/client';
import { LoggerMessages, LoggerServices } from '~logger/constants';
import type {
	TShopItem,
	TShopNeuronsPack,
	TShopPromoCodeResponse,
	TShopSubscription,
} from '~services/api/credits/types';
import { convertToBackendId } from '~utils/ids';

import { PaywallContext } from './index';
import type { TPaymentConfig, TPaymentOpenConfig } from './types';
import { PaymentType } from './types';

const PaymentModal = dynamic(
	() =>
		import('../../components/Modals/PaymentModal').then(
			(mod) => mod.PaymentModal
		),
	{ ssr: false }
);

export const PaywallContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const annalsSendOpenStore = annalsCheckout.useOpenStore();
	//TODO: remove selectedItem state from provider. move to internal state
	const [selectedItem, setSelectedItem] = useState<TShopItem>();
	const [requestedCost, setRequestedCost] = useState(0);
	const paymentTypeRef = useRef<PaymentType>(undefined);
	const [isConfirmOpen, setIsConfirmOpen] = useState<boolean | undefined>(
		undefined
	);
	const [defaultPromocode, setDefaultPromocode] = useState<
		string | undefined
	>();
	const [paymentReason, setPaymentReason] = useState<PaymentReason>();
	const [paymentReasonType, setPaymentReasonType] =
		useState<PaymentReasonType>();
	const [botName, setBotName] = useState<string>();
	const [botId, setBotId] = useState<number | string>();
	const [promoCodeInfo, setPromoCodeInfo] = useState<
		TShopPromoCodeResponse | undefined
	>(undefined);

	const [hasSubscription] = useHasSubscription();
	const { isDemoUser } = useSession();
	const { openRegister } = useAuthActionsContext();

	const {
		isOpen: isNeuronsOpen,
		title: neuronsTitle,
		open: neuronsOpen,
		close: neuronsClose,
	} = useModal();
	const {
		isOpen: isSubscriptionOpen,
		title: subscriptionTitle,
		open: subscriptionOpen,
		close: subscriptionClose,
	} = useModal();

	const {
		purchasing,
		isPaymentFormOpened,
		tryToBuy,
		openPaymentForm,
		closePaymentForm,
	} = usePurchase({
		onPurchased() {
			closeNeurons(true);
			closeSubscription(true);
		},
	});

	const isShowPaymentForm = isPaymentFormOpened && !!selectedItem;

	const callbackDecorator =
		(originalCallback?: (isSuccess: boolean) => void) =>
		(isSuccess?: boolean) => {
			originalCallback?.(isSuccess || false);
		};

	const openSubscription = useCallback(
		(config: TPaymentOpenConfig) => () => {
			const {
				reason,
				reasonType,
				botName,
				defaultPromocode,
				botClientId,
				title,
				paymentType,
				callback,
			} = config;

			paymentTypeRef.current = paymentType || PaymentType.Subscription;

			void annalsSendOpenStore({
				bot_UserId: botClientId,
				reason,
				reasonType,
				type: paymentTypeRef.current,
			});

			defaultPromocode && setDefaultPromocode(defaultPromocode);
			reason && setPaymentReason(reason);
			reasonType && setPaymentReasonType(reasonType);
			botName && setBotName(botName);
			botClientId && setBotId(botClientId);

			return subscriptionOpen({ title, cb: callbackDecorator(callback) })();
		},
		[annalsSendOpenStore, subscriptionOpen]
	);

	const openNeurons = useCallback(
		(config: TPaymentOpenConfig) => () => {
			const {
				cost,
				reason,
				reasonType,
				botName,
				botClientId,
				defaultPromocode,
				title,
				paymentType,
				callback,
			} = config;

			paymentTypeRef.current = paymentType || PaymentType.Neurons;
			void annalsSendOpenStore({
				bot_UserId: botClientId,
				reason,
				reasonType,
				type: paymentTypeRef.current,
			});

			defaultPromocode && setDefaultPromocode(defaultPromocode);
			cost && setRequestedCost(cost);
			reason && setPaymentReason(reason);
			reasonType && setPaymentReasonType(reasonType);
			botName && setBotName(botName);
			botClientId && setBotId(botClientId);

			ClientLogger.info(
				LoggerServices.PaymentService,
				LoggerMessages.BuyNeuronsScreenOpened,
				{ requiredCost: cost }
			);
			return neuronsOpen({ title, cb: callbackDecorator(callback) })();
		},
		[annalsSendOpenStore, neuronsOpen]
	);

	const openPaywall = useCallback(
		(config: TPaymentConfig) => {
			if (hasSubscription || config.prefer === 'neurons') {
				openNeurons({
					...config,
					// title: config.texts?.neurons,
				})();
			} else {
				const updatedCallback = (success: boolean) => {
					if (!success) {
						openNeurons({
							...config,
							title: config.texts?.neurons,
						})();
					}
				};

				openSubscription({
					...config,
					title: config.texts?.subscription,
					callback:
						config.prefer === 'subscription'
							? config.callback
							: updatedCallback,
				})();
			}
		},
		[hasSubscription, openNeurons, openSubscription]
	);

	const openPaywallWithDemoValidation = useCallback(
		(config: TPaymentConfig) => () => {
			if (isDemoUser) {
				openRegister({
					cb: () => {
						openPaywall(config);
					},
					analytics: { inbound: RegisterInbound.PaywallOpen },
				});
			} else {
				openPaywall(config);
			}
		},
		[isDemoUser, openRegister, openPaywall]
	);

	const clear = useCallback(() => {
		setDefaultPromocode(undefined);
		setBotName(undefined);
		setBotId(undefined);
		setRequestedCost(0);
		setSelectedItem(undefined);
		setPaymentReason(undefined);
		setPaymentReasonType(undefined);
	}, []);

	const closeNeurons = useCallback(
		(success: boolean) => {
			clear();
			neuronsClose(success);
		},
		[clear, neuronsClose]
	);

	const closeSubscription = useCallback(
		(success: boolean) => {
			clear();
			subscriptionClose(success);
		},
		[clear, subscriptionClose]
	);

	const onOkConfirm = useCallback(() => {
		setIsConfirmOpen(false);
	}, []);

	const onCancelConfirm = useCallback(() => {
		closeSubscription(false);
		setIsConfirmOpen(undefined);
	}, [closeSubscription]);

	const value = useMemo(
		() => ({
			isOpenPaymentForm: isPaymentFormOpened,
			reason: paymentReason,
			reasonType: paymentReasonType,
			botClientId: botId,
			openPaywall: openPaywallWithDemoValidation,
		}),
		[
			botId,
			paymentReason,
			paymentReasonType,
			isPaymentFormOpened,
			openPaywallWithDemoValidation,
		]
	);

	const closeAllModals = useCallback(() => {
		closePaymentForm();
		neuronsClose(true);
		subscriptionClose(true);
		clear();
	}, [closePaymentForm, neuronsClose, subscriptionClose, clear]);

	const closeSubscriptionViaCloseButton = useCallback(() => {
		const closedCount = loadFromStorage(lsKeys.PAYWALL_CLOSED_COUNT, 0);
		if (
			closedCount >= MIN_COUNT_TO_SHOW_BENEFITS &&
			closedCount <= MAX_COUNT_TO_SHOW_BENEFITS
		) {
			setIsConfirmOpen(true);
		} else {
			closeSubscription(false);
		}
		if (
			isConfirmOpen === undefined &&
			closedCount <= MAX_COUNT_TO_SHOW_BENEFITS
		) {
			saveToStorage(lsKeys.PAYWALL_CLOSED_COUNT, closedCount + 1);
		}
	}, [isConfirmOpen, closeSubscription]);

	const closeNeuronsViaCloseButton = useCallback(() => {
		closeNeurons(false);
	}, [closeNeurons]);

	const buy = useCallback(() => {
		selectedItem &&
			void tryToBuy(selectedItem, promoCodeInfo, {
				reason: paymentReason,
				reasonType: paymentReasonType,
				paymentType: paymentTypeRef.current,
				botId: botId ? convertToBackendId(botId) : undefined,
			});
	}, [
		botId,
		paymentReason,
		paymentReasonType,
		promoCodeInfo,
		selectedItem,
		tryToBuy,
	]);

	return (
		<PaywallContext.Provider value={value}>
			{children}
			<BenefitsModal
				open={!!isConfirmOpen}
				reason={paymentReason}
				reasonType={paymentReasonType}
				onOk={onOkConfirm}
				onCancel={onCancelConfirm}
			/>
			<SubscriptionModal
				open={isSubscriptionOpen && !isConfirmOpen}
				locked={false}
				defaultPromocode={defaultPromocode}
				title={subscriptionTitle}
				selectedItem={selectedItem as TShopSubscription}
				promoCodeInfo={promoCodeInfo}
				purchasing={purchasing}
				onSelect={setSelectedItem}
				onPromoCodeApply={setPromoCodeInfo}
				onBuy={buy}
				onBuyWithForm={openPaymentForm}
				onClose={closeSubscriptionViaCloseButton}
			/>
			<NeuronsModal
				open={isNeuronsOpen}
				locked={false}
				title={neuronsTitle}
				defaultPromocode={defaultPromocode}
				selectedItem={selectedItem as TShopNeuronsPack}
				promoCodeInfo={promoCodeInfo}
				requestedCost={requestedCost}
				purchasing={purchasing}
				onSelect={setSelectedItem}
				onPromoCodeApply={setPromoCodeInfo}
				onBuy={buy}
				onBuyWithForm={openPaymentForm}
				onClose={closeNeuronsViaCloseButton}
			/>
			{isShowPaymentForm && (
				<Suspense fallback={null}>
					<PaymentModal
						item={selectedItem}
						promoCodeInfo={promoCodeInfo}
						reason={paymentReason}
						reasonType={paymentReasonType}
						botClientId={botId}
						botName={botName}
						onSuccess={closeAllModals}
						onClose={closePaymentForm}
					/>
				</Suspense>
			)}
		</PaywallContext.Provider>
	);
};
